/**
 * PARTICULIER OU SOCIÉTÉ — la règle, à un seul endroit.
 *
 * ## Ce que faisaient les formulaires
 *
 * Les deux éditeurs de client décidaient du type d'après ce qui MANQUAIT :
 *
 *     type = (client.siret || client.tvaIntra) ? "societe" : "particulier"
 *
 * Le mobile ne regardait même pas `typeClient`. Le bureau le regardait, mais
 * seulement pour reconnaître un particulier — une entreprise sans SIRET
 * retombait sur « particulier ».
 *
 * Deux conséquences, et la seconde est la grave.
 *
 * Les champs SIRET et TVA n'étant affichés qu'en mode « société », un client
 * entreprise SANS SIRET ouvrait sans le champ où saisir son SIRET. La règle
 * était circulaire : il fallait déjà avoir l'identifiant pour voir la case où
 * l'écrire.
 *
 * Et à l'enregistrement, le formulaire réécrivait `typeClient` d'après ce mode
 * déduit : l'entreprise devenait « particulier » EN BASE, sans que rien ne le
 * signale. Au 30 août 2026, 121 clients entreprise sans SIRET étaient dans ce
 * cas — chacun attendant qu'on l'ouvre pour être déclassé.
 *
 * ## La règle voulue
 *
 * Un particulier est un cas métier normal : il reste particulier. Mais dès
 * qu'on lui saisit un SIRET ou un numéro de TVA, il devient une société —
 * parce que c'est ce que ces identifiants signifient.
 *
 * La promotion est donc automatique. Le déclassement ne l'est pas : perdre le
 * statut de société efface des identifiants et sort le client de la
 * facturation électronique, ce qui se décide, ne se subit pas.
 */

export type TypeAffiche = 'particulier' | 'societe';
export type TypeEnregistre = 'INDIVIDUAL' | 'COMPANY';

/** Une valeur saisie compte-t-elle comme renseignée ? */
export function estRenseigne(valeur: string | null | undefined): boolean {
  return typeof valeur === 'string' && valeur.trim() !== '';
}

/**
 * Le type d'un client existant, À L'OUVERTURE du formulaire.
 *
 * Il vient de `typeClient`, et de lui seul. La présence ou l'absence d'un
 * SIRET ne dit rien du type : elle dit seulement si on l'a saisi.
 */
export function typeÀLOuverture(client: {
  typeClient?: string | null;
} | null | undefined): TypeAffiche {
  if (!client) return 'societe';
  return client.typeClient === 'INDIVIDUAL' ? 'particulier' : 'societe';
}

/**
 * Le type APRÈS saisie — la promotion demandée.
 *
 * Un identifiant renseigné fait passer en société. Rien ne fait l'inverse :
 * effacer un SIRET ne déclasse pas, sans quoi une correction de frappe
 * suffirait à sortir un client de la facturation électronique.
 */
export function typeAprèsSaisie(
  typeActuel: TypeAffiche,
  siret: string | null | undefined,
  tvaIntra: string | null | undefined
): TypeAffiche {
  if (estRenseigne(siret) || estRenseigne(tvaIntra)) return 'societe';
  return typeActuel;
}

/** Ce que l'on écrit en base. */
export function typeEnregistré(type: TypeAffiche): TypeEnregistre {
  return type === 'particulier' ? 'INDIVIDUAL' : 'COMPANY';
}

/**
 * Un client peut-il recevoir une facture ÉLECTRONIQUE ?
 *
 * La réforme française vise les échanges entre assujettis. Un particulier en
 * est hors ; une société ne l'est qu'à condition d'avoir un identifiant, seul
 * moyen de la joindre sur le réseau — l'adresse d'acheminement se construit
 * à partir du SIREN ou du SIRET.
 *
 * Cette fonction ne dit PAS que le client est joignable : cela, seul l'annuaire
 * peut le confirmer. Elle dit qu'il a de quoi être cherché.
 */
export function peutRecevoirUneFactureElectronique(client: {
  typeClient?: string | null;
  siret?: string | null;
}): boolean {
  if (typeÀLOuverture(client) !== 'societe') return false;
  const chiffres = (client.siret ?? '').replace(/[^0-9]/g, '');
  return chiffres.length === 9 || chiffres.length === 14;
}
